
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { registerSchema } from "@/components/auth/register/schema";
import { useTransactionalEmail } from "@/hooks/use-transactional-email";

export type RegisterFormData = z.infer<typeof registerSchema>;

interface UseRegistrationProps {
  fromThankYou?: boolean;
  waitlistData?: boolean;
}

export function useRegistration({ fromThankYou = false, waitlistData = false }: UseRegistrationProps = {}) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { sendEmail } = useTransactionalEmail();

  const handleSignUp = async (data: RegisterFormData) => {
    try {
      setIsLoading(true);
      
      // Create the new user account
      const { data: userData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            first_name: data.firstName,
            last_name: data.lastName
          }
        }
      });

      if (error) throw error;

      // Check if email confirmation is required
      const emailConfirmationRequired = userData?.user?.identities?.length === 0 || 
                                       (userData?.user?.identities && userData?.user?.identities[0]?.identity_data?.email_verified === false);
      
      toast.success("Account created successfully!");
      
      // Send welcome email - this should happen ONLY at registration time
      try {
        const dashboardUrl = `${window.location.origin}/dashboard`;
        
        await sendEmail({
          type: 'welcome',
          data: {
            firstName: data.firstName || 'there',
            email: data.email,
            dashboardUrl
          }
        });
        
        console.log("Welcome email sent successfully");
      } catch (emailError) {
        console.error("Failed to send welcome email:", emailError);
        // Don't block the signup flow if email sending fails
      }
      
      // Flag to track when a user just completed authentication
      sessionStorage.setItem('justCompletedAuth', 'true');
      
      if (emailConfirmationRequired) {
        // Redirect to email verification page
        navigate('/auth/verify-email');
        return;
      }
      
      // Email verification not required, continue with normal flow
      // Force redirect to dashboard after successful signup
      if (fromThankYou) {
        console.log("Redirecting to dashboard from ThankYou page");
        // Use window.location.href for a full page reload to ensure auth state is refreshed
        window.location.href = '/dashboard';
        return;
      }
      
      // Handle normal flow for other cases
      const params = new URLSearchParams(window.location.search);
      const redirectTo = params.get('redirectTo');
      
      if (waitlistData) {
        // We'll just redirect to the thank you page with the right parameters
        const queryString = window.location.search;
        navigate(`/thank-you${queryString}`);
      } else if (redirectTo) {
        navigate(`/${redirectTo}`);
      } else {
        // Default to dashboard for any standard registration
        navigate("/dashboard");
      }
    } catch (error: any) {
      console.error("Registration error:", error);
      
      if (error.message.includes("already been registered")) {
        toast.error("This email is already registered", {
          description: "Please use a different email or try logging in."
        });
      } else {
        toast.error("Failed to create account", {
          description: error.message || "Please try again later."
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    handleSignUp
  };
}
